import React from 'react';
import { Icon } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import ImportProcess from '../model';
import { Table, Checkbox } from '@weezevent/nacre';

import Style from './analysis.module.css';

function ErrorInfo({ list }) {
    const { t } = useTranslation();

    return (
        <ul>
            {list.map((item, index) => (
                <li key={index}>
                    <span className="">{t([`ticket.attendees.import.errors.details.${item.field}`, item.field])}</span> {item.message}
                </li>
            ))}
        </ul>
    );
}

function ErrorItem({ error }) {
    const { t } = useTranslation();

    let { message, field } = error;
    let details = null;

    if (Array.isArray(error)) {
        message = error[0].message;
        field = error[0].field;
        // Remove 1st item
        details = <ErrorInfo list={error.slice(1)} />;
    }

    return (
        <Table.Row>
            <Table.Cell>{t([`ticket.attendees.import.errors.fields.${field}`, field])}</Table.Cell>
            <Table.Cell>{t([`ticket.attendees.import.errors.messages.${message}`, message])}</Table.Cell>
            <Table.Cell>{details}</Table.Cell>
        </Table.Row>
    );
}

function ErrorDisplayer({ errors }) {
    const { t } = useTranslation();
    return (
        <Table>
            <Table.Header>
                <Table.Row>
                    <th>{t('ticket.attendees.import.errors.fields.header')}</th>
                    <th>{t('ticket.attendees.import.errors.messages.header')}</th>
                    <th>{t('ticket.attendees.import.errors.details.header')}</th>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {errors.map((error, i) => (
                    <ErrorItem key={i} error={error} />
                ))}
            </Table.Body>
        </Table>
    );
}

function ErrorDetails({ errors = {} }) {
    return (
        <Table>
            <Table.Header>
                <Table.Row>
                    <th>
                        <Trans i18nKey="nacre.ImportController.analysis.header.todo">Todo</Trans>
                    </th>
                    <th>
                        <Trans i18nKey="nacre.ImportController.analysis.header.line">Lines</Trans>
                    </th>
                    <th>
                        <Trans i18nKey="nacre.ImportController.analysis.header.error">Error</Trans>
                    </th>
                </Table.Row>
            </Table.Header>

            <Table.Body>
                {Object.entries(errors).map(([line, errors], i) => {
                    return (
                        <Table.Row key={i}>
                            <Table.Cell style={{ textAlign: 'center' }}>
                                <Checkbox />
                            </Table.Cell>
                            <Table.Cell key={line} style={{ fontWeight: 'bold', fontSize: '1.1em', textAlign: 'center' }}>
                                {line}
                            </Table.Cell>
                            <Table.Cell>
                                <ErrorDisplayer errors={errors} />
                            </Table.Cell>
                        </Table.Row>
                    );
                })}
            </Table.Body>
        </Table>
    );
}
ErrorDetails.propTypes = {
    errors: PropTypes.object.isRequired
};

function AnalysisLine({ children, icon, iconStyle = {} }) {
    return (
        <p>
            {icon}
            <span className="text-details">{children}</span>
        </p>
    );
}
AnalysisLine.propTypes = {
    children: PropTypes.node.isRequired,
    icon: PropTypes.node.isRequired
};

function IconWeez({ icon, className = '' }) {
    return <Icon className={`${className} icon-weez`} name={icon} />;
}
IconWeez.propTypes = {
    icon: PropTypes.string.isRequired,
    className: PropTypes.string
};

export default function ShowAnalysis({ step, importProcess, showDetails, toggleDetails }) {
    const totalRowsCount = importProcess.countTotalRows();
    const errorRowsCount = importProcess.countErrorRows();
    const createdRowsCount = importProcess.countCreatedRows();

    return (
        <React.Fragment>
            <AnalysisLine icon={<IconWeez icon="tasks" />} iconS>
                <Trans i18nKey="nacre.ImportController.analysis.summary.total_line" values={{ count: totalRowsCount }} count={totalRowsCount}>
                    %count% treated lines
                </Trans>
            </AnalysisLine>
            <AnalysisLine icon={<IconWeez icon="times circle outline" className={Style['red']} />}>
                <Trans i18nKey="nacre.ImportController.analysis.summary.invalid_line" values={{ count: errorRowsCount }} count={errorRowsCount}>
                    %count% invalid lines
                </Trans>
                {errorRowsCount ? (
                    <label className="details" onClick={toggleDetails}>
                        <Trans i18nKey="import.summary.details">Detail</Trans>
                    </label>
                ) : null}
            </AnalysisLine>
            {showDetails ? <ErrorDetails errors={importProcess.getErrorGroups()} /> : null}
            <AnalysisLine icon={<IconWeez icon="check circle outline" className={Style['green']} />}>
                <Trans i18nKey="nacre.ImportController.analysis.summary.valid_line" values={{ count: createdRowsCount }} count={createdRowsCount}>
                    %count% valid lines
                </Trans>
            </AnalysisLine>
        </React.Fragment>
    );
}

ShowAnalysis.propTypes = {
    step: PropTypes.oneOf(['import-process-confirm', 'import-process-done']),
    importProcess: PropTypes.instanceOf(ImportProcess).isRequired,
    showDetails: PropTypes.bool.isRequired,
    toggleDetails: PropTypes.func.isRequired
};
